import { Injectable } from '@angular/core';
import { BehaviorSubject, EMPTY, forkJoin, Observable, ReplaySubject } from 'rxjs';
import { finalize, map } from 'rxjs/operators';
import { LogIn } from '@shared/models/login';
import { ApiService } from '@shared/apis';
import { Router } from '@angular/router';
import sharedStorage from '@shared/services/shared-storage';
import SharedStorage from '@shared/services/shared-storage';
import * as _ from 'lodash-es';
import Role from '@shared/constants/role';
import { Company } from '@shared/enums/company';
import * as moment from 'moment';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { Constants } from '@shared/constants/constants';
import { SYSTEM } from '@shared/constants/system';

@Injectable()
export class AuthService {
  readonly tokenKeyName = 'token';
  readonly companyKeyName = 'company';
  readonly companiesKeyName = 'companies';
  readonly ticketFilterKeyName = 'filter';
  readonly tokenRenewCutoff = 60 * 1000; // 60 sec left token is value

  public token$ = new BehaviorSubject({});
  public redirectUrl: any;
  readonly systemKeyName = 'system';
  //Common Options
  dealerCodes: any[];
  hqDealers: any[];
  private hqDealers$ = new ReplaySubject<any[]>(1);
  private codesRequested = false;
  bpDealers: any[];
  cpDealers: any[];
  companyCodes: any[];
  private currentCompany: string;
  private token: any;
  private isSystem: string;

  constructor(
    private apiService: ApiService,
    private router: Router,
    private bsModalRef: BsModalRef,
  ) {
    this.loadToken();
  }

  get deviceId(): string {
    return localStorage.getItem('deviceId');
  }

  set deviceId(value: string) {
    if (!value) {
      localStorage.removeItem('deviceId');
    } else {
      localStorage.setItem('deviceId', value);
    }
  }

  login(data: LogIn): Observable<boolean> {
    return this.apiService.auth.login(data)
      .pipe(
        map(result => {
          this.setCompanies([]);
          this.setCompany(null);
          result.loginAt = moment().add(60, 'seconds');
          this.setToken(result);
          return result;
        })
      );
  }

  otpLogin(username, code, tfaToken, trust): Observable<boolean> {
    return this.apiService.auth.otpLogin(username, code, tfaToken, trust)
      .pipe(
        map(result => {
          this.setToken(result);
          this.deviceId = result['deviceId'];
          return result;
        })
      );
  }

  resendOtp(username, tfaToken): Observable<boolean> {
    return this.apiService.auth.resendOtp(username, tfaToken)
      .pipe(
        map(result => {
          this.setToken(result);
          return result;
        })
      );
  }

  logout(): void {
    this.setToken(null);
    this.setCompany(null);
    this.setCompanies(null);
    this.setSystem(null);
    this.clearCodes();
  }

  setToken(token): void {
    if (token?.token?.access_token) {
      const tokenStr = JSON.stringify(token);
      sharedStorage.setItem(this.tokenKeyName, tokenStr);
    } else {
      sharedStorage.removeItem(this.tokenKeyName);
      SharedStorage.removeItem(this.ticketFilterKeyName);
    }

    this.processToken(token);
  }

  setCompany(company: string): void {
    // dealer lists are company-scoped, so a switch must not serve the old one
    if (this.currentCompany && company !== this.currentCompany) {
      this.clearCodes();
    }
    this.currentCompany = company;
    if (company) {
      localStorage.setItem(this.companyKeyName, company);
    } else {
      localStorage.removeItem(this.companyKeyName);
    }
  }

  setCompanies(companies: any[]): void {
    if (companies && companies.length > 0) {
      localStorage.setItem(this.companiesKeyName, companies.toString());
    } else {
      localStorage.removeItem(this.companiesKeyName);
    }
  }

  setSystem(system: string): void {
    this.isSystem = system;
    if (system) {
      localStorage.setItem(this.systemKeyName, system);
    } else {
      localStorage.removeItem(this.systemKeyName);
    }
  }

  getCompanies() {
    const companies = localStorage.getItem(this.companiesKeyName);
    if (companies) {
      return companies.split(',');
    }
    return [];
  }

  getToken() {
    if (this.token) {
      return this.token;
    }

    return this.loadToken();
  }

  loadToken() {
    const tokenStr = sharedStorage.getItem(this.tokenKeyName);
    let token = null;

    if (tokenStr) {
      token = JSON.parse(tokenStr);
      this.processToken(token);
    }

    return token;
  }

  refreshToken(): Observable<any> {
    const { token } = this.getToken();

    if (!token || !token.refresh_token) {
      return EMPTY;
    }

    return this.apiService.auth.refreshToken(token.refresh_token);
  }

  getValidAccessTokenPromise() {
    return new Promise((resolve, reject) => {
      const token = this.getToken();

      if (moment(token.loginAt).diff(moment(), 'seconds') > 0) {
        // token has 60 sec to be expired
        return resolve(token.access_token);
      }

      this.apiService.auth.refreshToken(token.refresh_token).subscribe(res => {
        this.setToken(res);
        resolve(res.access_token);
      }, err => {
        reject(err);
      });
    })
  }

  isAuthenticated(): boolean {
    return this.getToken() != null;
  }

  getAccessToken(): string {
    const { token } = this.getToken() || '';
    return token ? token.access_token : ''
  }

  hasRole(roles, company = this.getCurrentCompany()) {
    if (!this.token) {
      return false;
    }

    if (!Array.isArray(roles)) {
      roles = [roles];
    }

    if (_.isEmpty(roles)) {
      return false;
    }

    const getRoles = this.token.roles || [];
    return getRoles.some(role => {
      if (company && role.company !== company) {
        return false;
      }
      const groups = role.groups || [];
      if (groups.includes(Role.admin)) {
        return true;
      }

      return groups.some(authority => {
        return roles.includes(authority);
      });
    });
  }

  hasRoleInCompany(role, company = this.getCurrentCompany()) {
    const roles = this.getRoles().filter(roles => roles.company === company);
    return roles.filter(r => r.groups.includes(role))?.length > 0;
  }

  hasType(roles) {
    if (!this.token) {
      return false;
    }

    if (!Array.isArray(roles)) {
      roles = [roles];
    }

    if (_.isEmpty(roles)) {
      return false;
    }

    const { userType } = this.getToken();

    return roles.includes(userType);
  }

  getUserType() {
    const { userType } = this.getToken();
    return userType;
  }

  isHq() {
    return this.getToken().hq || false;
  }


  getRoles() {
    if (!this.token) {
      return false;
    }
    return this.token.roles;
  }

  getRolesByCompany(company?: string) {
    if (!this.token) {
      return false;
    }
    let findRole: any;
    if (company) {
      findRole = this.token.roles.find(f => f.company === company);
    } else {
      findRole = this.token.roles.find(f => f.company === this.getCurrentCompany());
    }
    return _.get(findRole, 'groups', []);
  }


  getCurrentCompany(): string {
    if (this.currentCompany) {
      return this.currentCompany || Company.Sea;
    }

    this.currentCompany = localStorage.getItem(this.companyKeyName);
    return this.currentCompany;
  }

  getCurrentCountry(company) {
    return Constants.companies.find(c => c.code === company)?.country || 'us';
  }

  getIsSystem(): string {
    if (this.isSystem) {
      return this.isSystem || SYSTEM.DEALER;
    }

    this.isSystem = localStorage.getItem(this.systemKeyName);
    return this.isSystem;
  }

  getUser() {
    const token = this.getToken();
    const user: any = {
      firstName: token?.firstName,
      lastName: token?.lastName,
      username: token?.username,
      key: token?.token?.user.id,
      organization: token?.organization,
      hq: token?.hq,
      userType: token?.userType,
      roles: token?.roles,
      employeeNo: token?.employeeNo,
      doNotAllowComment: token?.doNotAllowComment || false,
    }

    return user || {};
  }

  getQRCode(): Observable<any> {
    return this.apiService.auth.getQRCode()
      .pipe(
        map(result => {
          return result;
        })
      );
  }

  // region # Authenticator

  registerMfa(code): Observable<boolean> {
    return this.apiService.auth.registerMfa(code)
      .pipe(
        map(result => {
          return result.result;
        })
      );
  }  // endregion

  mfaLogin(username, code, tfaToken, trust?): Observable<boolean> {
    return this.apiService.auth.mfaLogin(username, code, tfaToken, trust)
      .pipe(
        map(result => {
          this.setToken(result);
          this.deviceId = result['deviceId'];
          return result;
        })
      );
  }

  checkHasAdminViewerRole() {
    const roles = this.getRoles();
    return roles.some(role => role.groups.some(group => group.includes(Role.adminViewer)));
  }

  setCodes() {
    this.codesRequested = true;
    const getHQDealers = this.apiService.dealer.getHQDealers();
    const getBpDealers = this.apiService.dealer.getBpDealers();
    const getCpDealers = this.apiService.dealer.getCpDealers();
    const getCompanyCodes = this.apiService.company.getCompanyCodes()
    forkJoin([getHQDealers, getBpDealers, getCpDealers, getCompanyCodes])
      .pipe(finalize(() => {
        console.log('[AuthService] Finished loading dealer codes in authService');
      }))
      .subscribe(([hqDealers, bpDealers, cpDealers, companyCodes]) => {
        this.hqDealers = hqDealers;
        this.bpDealers = bpDealers;
        this.cpDealers = cpDealers;
        this.companyCodes = companyCodes;
        this.hqDealers$.next(hqDealers || []);
      }, () => this.hqDealers$.next([]));
    return this.companyCodes;
  }

  getHQDealers() {
    return this.hqDealers;
  }

  // The header kicks off setCodes() on app entry, but a caller that opens before
  // that resolves gets undefined from getHQDealers(). This replays the list to
  // whoever asks and loads it on demand when nothing has asked yet, so callers do
  // not have to poll (see b2b ticket.component, which retries on a timer).
  getHqDealers$(): Observable<any[]> {
    if (!this.codesRequested) {
      this.setCodes();
    }
    return this.hqDealers$.asObservable();
  }

  clearCodes() {
    this.codesRequested = false;
    this.hqDealers = undefined;
    this.bpDealers = undefined;
    this.cpDealers = undefined;
    this.hqDealers$ = new ReplaySubject<any[]>(1);
  }

  getBpDealers() {
    return this.bpDealers;
  }

  getCpDealers(hqBpNo?) {
    if(this.cpDealers) {
      return this.cpDealers.filter(cpDealer => cpDealer.hqBpNo === hqBpNo);
    } else {}
    return [];
  }

  getCompanyCodes() {
    return this.companyCodes;
  }


  private processToken(token): void {
    this.token = token;

    this.token$.next(this.token);

    if (token === null) {
      this.router.navigate(['/auth/login']);
    }
  }

  // endregion
}
